import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Users, Building2, Lock, MapPin } from 'lucide-react';

import { supabase } from '../supabaseClient';
import ConfidenceDonut from './ConfidenceDonut';
import { useNavigate } from 'react-router-dom';

const CompanyInsightsModal = ({ companyName, isOpen, onClose, isPremium }) => {
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isOpen || !companyName) return;

        const fetchInsights = async () => {
            setLoading(true);
            try {
                console.log('[CompanyInsightsModal] Fetching insights for:', JSON.stringify(companyName));
                const { data, error } = await supabase.rpc('get_company_recruiter_insights', {
                    p_entreprise: companyName
                });
                console.log('[CompanyInsightsModal] RPC response data:', JSON.stringify(data));
                console.log('[CompanyInsightsModal] RPC response error:', error);
                if (error) throw error;
                setInsights(data);
            } catch (err) {
                console.error('Error fetching company insights:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchInsights();
    }, [isOpen, companyName]);

    if (!isOpen) return null;

    // Donut chart colors for role types
    const ROLE_COLORS = {
        'RH': '#8b5cf6',
        'Opérationnel': '#3b82f6',
        'CEO': '#f59e0b',
        'Direction': '#ef4444',
    };

    const getRoleColor = (type) => ROLE_COLORS[type] || '#94a3b8';

    // SVG donut for role distribution
    const RoleDistributionDonut = ({ distribution }) => {
        const size = 120;
        const strokeWidth = 16;
        const radius = (size - strokeWidth) / 2;
        const circumference = 2 * Math.PI * radius;
        const center = size / 2;

        let cumulativeOffset = 0;

        return (
            <svg width={size} height={size} className="transform -rotate-90">
                {distribution.map((item, idx) => {
                    const segmentLength = (item.percentage / 100) * circumference;
                    const dashOffset = circumference - segmentLength;
                    const rotation = (cumulativeOffset / circumference) * 360;
                    cumulativeOffset += segmentLength;

                    return (
                        <circle
                            key={idx}
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="none"
                            stroke={getRoleColor(item.type)}
                            strokeWidth={strokeWidth}
                            strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
                            strokeDashoffset={-cumulativeOffset + segmentLength}
                            style={{ transition: 'all 0.6s ease' }}
                        />
                    );
                })}
            </svg>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Modal */}
            <div
                className="relative bg-white rounded-[28px] w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 fade-in duration-200 modal-scroll"
                onClick={(e) => e.stopPropagation()}
                style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e1 transparent',
                }}
            >
                <style>{`
                    .modal-scroll::-webkit-scrollbar { width: 5px; }
                    .modal-scroll::-webkit-scrollbar-track { background: transparent; margin: 14px 0; }
                    .modal-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 9999px; }
                    .modal-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                    .modal-scroll::-webkit-scrollbar-button { display: none; }
                `}</style>
                <div className="sticky top-0 bg-white/95 backdrop-blur-sm rounded-t-[28px] px-6 pt-6 pb-4 border-b border-slate-100 z-10">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center px-3 py-1.5 bg-slate-900 rounded-full">
                                <img src="/assets/Trouve+white.png" alt="Trouve+" className="h-5 w-auto object-contain" />
                            </div>
                            <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600 uppercase tracking-wide">
                                Insights Recrutement
                            </span>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                        >
                            <X className="w-4 h-4 text-slate-500" />
                        </button>
                    </div>
                    <h2 className="text-xl font-black text-slate-900 mt-3 leading-tight">{companyName}</h2>
                </div>

                {/* Content */}
                <div className="px-6 py-6">
                    {!isPremium ? (
                        /* Non-premium locked state */
                        <div className="text-center py-10">
                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Lock className="w-7 h-7 text-slate-400" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 mb-2">Fonctionnalité Premium</h3>
                            <p className="text-sm text-slate-500 mb-6 max-w-xs mx-auto">
                                Accédez aux insights de recrutement pour optimiser vos candidatures spontanées.
                            </p>
                            <button
                                onClick={() => { onClose(); navigate('/trouve-plus'); }}
                                className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-colors shadow-lg"
                            >
                                <span className="flex items-center gap-2">
                                    Découvrir <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400 font-black">Trouve+</span>
                                </span>
                            </button>
                        </div>
                    ) : loading ? (
                        /* Loading state */
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                            <div className="w-10 h-10 border-3 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                            <p className="text-sm text-slate-400 font-medium">Analyse en cours...</p>
                        </div>
                    ) : !insights || insights.total_analyzed === 0 ? (
                        /* No data state */
                        <div className="text-center py-10">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <TrendingUp className="w-7 h-7 text-slate-300" />
                            </div>
                            <h3 className="text-base font-bold text-slate-600 mb-1">Données insuffisantes</h3>
                            <p className="text-sm text-slate-400">
                                Nous n'avons pas encore suffisamment de données sur les recrutements de cette entreprise.
                            </p>
                        </div>
                    ) : (
                        /* Insights content */
                        <div className="space-y-6">
                            {/* Key finding */}
                            <div className="bg-gradient-to-br from-blue-50 to-violet-50 rounded-2xl p-5 border border-violet-100/50">
                                <div className="flex items-start gap-3 mb-3">
                                    <Users className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-bold text-slate-900 text-sm mb-2">Analyse des recrutements</h3>
                                        <p className="text-sm text-slate-700 leading-relaxed">
                                            D'après nos analyses, les recrutements de VIE chez{' '}
                                            <span className="font-bold">{insights.search_company_name || companyName}</span>{' '}
                                            sont en moyenne gérés par des profils{' '}
                                            <span className="font-bold text-violet-700">{insights.dominant_role_type}</span>.
                                        </p>
                                    </div>
                                </div>

                                {/* Sample roles */}
                                {insights.sample_roles && insights.sample_roles.length > 0 && (
                                    <div className="mt-3 pl-8">
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Intitulés détectés</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {insights.sample_roles.filter(Boolean).slice(0, 4).map((role, idx) => (
                                                <span
                                                    key={idx}
                                                    className="px-2.5 py-1 bg-white rounded-lg text-xs font-semibold text-slate-600 border border-slate-200"
                                                >
                                                    {role}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Commercial name */}
                            {insights.search_company_name && (
                                <div className="flex items-center gap-3 px-1">
                                    <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                    <p className="text-sm text-slate-600">
                                        Nom commercial :{' '}
                                        <span className="font-bold text-slate-800">{insights.search_company_name}</span>
                                    </p>
                                </div>
                            )}

                            {/* Recruiter location */}
                            {insights.dominant_location && (
                                <div className="bg-white rounded-xl p-4 border border-slate-100">
                                    <div className="flex items-start gap-3">
                                        <MapPin className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <p className="text-sm text-slate-700 leading-relaxed mb-3">
                                                Le recrutement est majoritairement géré{' '}
                                                {insights.dominant_location === 'France' ? (
                                                    <span className="font-bold text-violet-700">depuis la France (siège)</span>
                                                ) : (
                                                    <span className="font-bold text-blue-700">en local (pays du VIE)</span>
                                                )}
                                            </p>
                                            <div className="flex gap-2">
                                                {insights.location_distribution.map((loc, idx) => (
                                                    <div key={idx} className="flex-1 bg-slate-50 rounded-lg px-3 py-2 text-center">
                                                        <span className="text-xs font-bold text-slate-700 block">
                                                            {loc.location === 'France' ? '🇫🇷 France' : '🌍 Local'}
                                                        </span>
                                                        <span className="text-lg font-black text-slate-800">{loc.percentage}%</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Charts row */}
                            <div className="flex items-center justify-around py-4">
                                {/* Role type donut */}
                                <div className="flex flex-col items-center gap-3">
                                    <RoleDistributionDonut distribution={insights.role_type_distribution} />
                                    {/* Legend */}
                                    <div className="flex flex-col gap-1.5">
                                        {insights.role_type_distribution.map((item, idx) => (
                                            <div key={idx} className="flex items-center gap-2">
                                                <div
                                                    className="w-3 h-3 rounded-full"
                                                    style={{ backgroundColor: getRoleColor(item.type) }}
                                                />
                                                <span className="text-xs font-semibold text-slate-600">
                                                    {item.type} ({item.percentage}%)
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Répartition</span>
                                </div>

                                {/* Confidence donut */}
                                <div className="relative">
                                    <ConfidenceDonut
                                        score={insights.avg_confidence || 0}
                                        size={100}
                                        strokeWidth={10}
                                        label="Confiance"
                                    />
                                </div>
                            </div>

                            {/* Stats footer */}
                            <div className="bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between">
                                <span className="text-xs text-slate-500">
                                    Basé sur <span className="font-bold text-slate-700">{insights.total_analyzed}</span> offre(s) analysée(s)
                                </span>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    confidence &gt; 70%
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompanyInsightsModal;
